import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import DraggableModal from "./DraggableModal";
import BulkDeleteDialog from "./BulkDeleteDialog";
import SearchSelect from "./SearchSelect";
import FileDropZone from "./FileDropZone";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import { computeCertUsage as sharedComputeCertUsage } from "./exportCertUsage";
import ExportButton from "./ExportButton";
import { intOr, s as xs } from "./xlsxExport";
import { loadFromSupabase, saveToSupabase, deleteFromSupabase, getCached } from "../supabaseDB";
import { htmlToPdfBlob } from "../pdfRenderer";
import EmailDialog from "./EmailDialog";
import { loadCompanyProfile } from "./CompanyProfile";
import { filterContactsByCategory, appendDisclosureByAccount } from "./emailHelpers";

/* ======================== TRANSLATIONS ======================== */
const T = {
  en: {
    title: "Export Certificates", add: "New certificate", edit: "Edit certificate",
    search: "Search by number, shipper, or product...",
    showing: "Showing", of: "of", total: "certificates",
    noResults: "No certificates match",
    // Table headers
    certNumber: "Certificate #", shipper: "Shipper", product: "Product",
    certifier: "Certifying Body", period: "Period", from: "From", to: "To",
    totalInvoices: "Total", used: "Used", available: "Available",
    status: "Status", document: "Document",
    // Statuses
    active: "Active", expiringSoon: "Expiring Soon", exhausted: "Exhausted",
    expired: "Expired",
    // Alerts
    alertLow: "Running low — 3 invoices remaining",
    alertCritical: "Critical — last invoice available",
    alertExhausted: "Exhausted — request new certificate from shipper",
    alertExpired: "Certificate period has expired",
    // Form
    selectShipper: "Select shipper",
    selectProduct: "Select product",
    shipperProducts: "Shipper's certified products",
    otherProducts: "Other organic products",
    importer: "Importer",
    importerFixed: "Ground Fresh International, Inc.",
    periodFrom: "Period from", periodTo: "Period to",
    totalContainers: "Total invoices / containers",
    usedInvoices: "Used invoices",
    uploadDoc: "Upload certificate PDF",
    changeDoc: "Change document",
    viewDoc: "View document", removeDoc: "Remove document",
    notes: "Notes",
    // Sections
    identity: "Certificate Information",
    invoiceTracking: "Invoice Tracking",
    invoiceList: "Assigned Invoices",
    noInvoices: "No invoices assigned yet",
    shipperInvoice: "Shipper Invoice #", shipmentLot: "Lot", assignDate: "Date",
    // Audit report
    printAudit: "Download Audit Report (PDF)",
    auditSortBy: "Sort by", auditSortInvoice: "Invoice #", auditSortLot: "Lot", auditSortDate: "Date",
    auditPdfError: "Could not generate the PDF. Try again.",
    auditReportTitle: "Export Certificate Audit Report",
    certificateInfo: "Certificate Information",
    importer: "Importer",
    certifyingBody: "Certifying Body",
    // Actions
    save: "Save", cancel: "Cancel", delete: "Delete", editBtn: "Edit", close: "Close",
    confirmDel: "Delete this certificate? This will remove all invoice tracking data.",
    yes: "Yes, delete", no: "Cancel",
    confirmExit: "You have unsaved changes. Exit without saving?",
    yesExit: "Yes, exit", noExit: "Keep editing",
    saved: "Certificate saved", deleted: "Certificate deleted",
    required: "Required", fillRequired: "Please fill all required fields",
    dupCert: "A certificate with this number already exists",
    // Filters
    all: "All", allShippers: "All shippers", allProducts: "All products",
    filterStatus: "Status", filterShipper: "Shipper", filterProduct: "Product",
    showExpired: "Show expired",
  },
  es: {
    title: "Certificados de Exportación", add: "Nuevo certificado", edit: "Editar certificado",
    search: "Buscar por número, proveedor o producto...",
    showing: "Mostrando", of: "de", total: "certificados",
    noResults: "Sin resultados",
    certNumber: "No. Certificado", shipper: "Proveedor", product: "Producto",
    certifier: "Certificadora", period: "Período", from: "Desde", to: "Hasta",
    totalInvoices: "Total", used: "Usadas", available: "Disponibles",
    status: "Estado", document: "Documento",
    active: "Activo", expiringSoon: "Por agotarse", exhausted: "Agotado",
    expired: "Expirado",
    alertLow: "Por agotarse — quedan 3 facturas",
    alertCritical: "Crítico — última factura disponible",
    alertExhausted: "Agotado — solicitar nuevo certificado al proveedor",
    alertExpired: "El período del certificado ha expirado",
    selectShipper: "Seleccionar exportador",
    selectProduct: "Seleccionar producto",
    shipperProducts: "Productos certificados del exportador",
    otherProducts: "Otros productos orgánicos",
    importer: "Importador",
    importerFixed: "Ground Fresh International, Inc.",
    periodFrom: "Período desde", periodTo: "Período hasta",
    totalContainers: "Total facturas / containers",
    usedInvoices: "Facturas usadas",
    uploadDoc: "Subir certificado PDF",
    changeDoc: "Cambiar documento",
    viewDoc: "Ver documento", removeDoc: "Quitar documento",
    notes: "Notas",
    identity: "Información del Certificado",
    invoiceTracking: "Control de Facturas",
    invoiceList: "Facturas Asignadas",
    noInvoices: "Sin facturas asignadas",
    shipperInvoice: "Factura del Exportador #", shipmentLot: "Lote", assignDate: "Fecha",
    // Audit report
    printAudit: "Descargar Reporte de Auditoría (PDF)",
    auditSortBy: "Ordenar por", auditSortInvoice: "Factura #", auditSortLot: "Lote", auditSortDate: "Fecha",
    auditPdfError: "No se pudo generar el PDF. Intenta de nuevo.",
    auditReportTitle: "Reporte de Auditoría del Certificado de Exportación",
    certificateInfo: "Información del Certificado",
    importer: "Importador",
    certifyingBody: "Entidad Certificadora",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", editBtn: "Editar", close: "Cerrar",
    confirmDel: "¿Eliminar este certificado? Se perderá todo el registro de facturas.",
    yes: "Sí, eliminar", no: "Cancelar",
    confirmExit: "Tienes cambios sin guardar. ¿Salir sin guardar?",
    yesExit: "Sí, salir", noExit: "Seguir editando",
    saved: "Certificado guardado", deleted: "Certificado eliminado",
    required: "Requerido", fillRequired: "Completa todos los campos requeridos",
    dupCert: "Ya existe un certificado con este número",
    all: "Todos", allShippers: "Todos los exportadores", allProducts: "Todos los productos",
    filterStatus: "Estado", filterShipper: "Exportador", filterProduct: "Producto",
    showExpired: "Mostrar expirados",
  },
};

/* ======================== CONSTANTS ======================== */
const STORAGE_KEY = "gfi-export-certs";
const inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", transition: "all 0.2s", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

/* ======================== HELPERS ======================== */
function loadLS(key, fallback) { try { const d = localStorage.getItem(key); return d ? JSON.parse(d) : fallback; } catch { return fallback; } }

/* Prefer the in-memory Supabase cache over the localStorage mirror —
   `certifications` is NOT in primeCatalogs()'s startup list, so a tab
   that never opened the Certifications module has a stale/empty local
   mirror and getShipperOrgCert silently finds nothing (Arturo: "al
   seleccionar Gomez Berries no me importa su certificadora"). Falls
   back to loadLS only while the cache genuinely hasn't loaded yet. */
function getSuppliers() {
  var cached = getCached("suppliers");
  var list = cached && cached.length > 0 ? cached : loadLS("gfi-suppliers", []);
  return list.filter(s => s.isActive !== false);
}
function getProducts() { return loadLS("gfi-products", []).filter(p => p.isActive !== false && (p.category === "Organic" || p.category === "OG")); }
function getCertifications() {
  var cached = getCached("certifications");
  return cached && cached.length > 0 ? cached : loadLS("gfi-certifications", []);
}

// Get organic certifier for a given supplier from certifications
function getShipperOrgCert(supplierId) {
  const certs = getCertifications();
  // Certifications store entityId as "shipper-{id}" or just the raw id
  /* Deliberately NOT filtered by expiry date — Arturo: "aunque el
     certificado orgánico esté vencido en fecha sigue siendo vigente si
     no está revocado". An organic cert past its anniversary date is
     still the shipper's real certifying body until GFI archives the
     record (isActive === false, the only "revoked" concept this app
     tracks — see CertificationRegistry.js). */
  return certs.find(c =>
    c.entityType === "shipper" &&
    (c.entityId === supplierId || c.entityId === `shipper-${supplierId}`) &&
    c.certType === "organic" &&
    c.isActive !== false
  );
}

// Get products certified for a shipper from their organic certification
function getShipperCertifiedProducts(supplierId) {
  const cert = getShipperOrgCert(supplierId);
  if (!cert || !cert.products) return [];
  return cert.products.map(p => p.productName).filter(Boolean);
}

function isExpired(cert) {
  if (!cert) return false;
  const endDate = cert.anniversaryDate || cert.validTo;
  if (!endDate) return false;
  return new Date(endDate) < new Date();
}

/* USED COUNT — single source of truth.
   The cert's "Used" count is DERIVED from gfi-shipments at read time:
   we scan every shipment and count it once if any of its lines references
   this cert (by exportCertId match preferred; by normalized
   exportCertNumber as fallback for legacy or imported records that didn't
   resolve the id). The legacy cert.usedInvoices array kept on the cert
   record is no longer authoritative — it stays as an editable history
   marker but the number/list shown in UI is always recomputed.

   The registry injects `usedCount` + `usageList` onto each cert before
   passing it downstream. usedOf() prefers the derived value and falls
   back to the legacy length only when the derived value is absent (e.g.
   buildAuditReportHTML invoked with a bare cert from somewhere new). */
/* Use the shared helper from exportCertUsage.js so ShipmentRegistry's
   dropdown and this module agree on what a "use" is (one unique shipper
   invoice that references the cert, by id or normalized number). */
var computeCertUsage = sharedComputeCertUsage;
function usedOf(cert) {
  return typeof cert.usedCount === "number" ? cert.usedCount : (cert.usedInvoices || []).length;
}

function getStatus(cert) {
  // Check period expiration first
  if (cert.periodTo) {
    const end = new Date(cert.periodTo);
    if (end < new Date()) return "expired";
  }
  // Check invoice availability
  const avail = (cert.totalInvoices || 0) - usedOf(cert);
  if (avail <= 0) return "exhausted";
  if (avail <= 1) return "expiringSoon";
  if (avail <= 3) return "expiringSoon";
  return "active";
}

function getAlertLevel(cert) {
  // Only alert for certificates with current valid period
  // Historical/expired certificates don't generate alerts
  if (cert.periodTo && new Date(cert.periodTo) < new Date()) return null;
  // Only alert if period has started (not future certificates)
  if (cert.periodFrom && new Date(cert.periodFrom) > new Date()) return null;
  const avail = (cert.totalInvoices || 0) - usedOf(cert);
  if (avail <= 0) return "exhausted";
  if (avail === 1) return "critical";
  if (avail <= 3) return "low";
  return null;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

function nextId(items) {
  const nums = items.map(i => parseInt((i.id || "").replace("EXC-", ""), 10)).filter(n => !isNaN(n));
  return `EXC-${String((Math.max(0, ...nums) + 1)).padStart(4, "0")}`;
}

const STATUS_STYLES = {
  active: { bg: "var(--color-badge-og-bg)", text: "var(--color-badge-og-text)", border: "var(--color-badge-og-border)" },
  expiringSoon: { bg: "var(--color-badge-cv-bg)", text: "var(--color-badge-cv-text)", border: "var(--color-badge-cv-border)" },
  exhausted: { bg: "rgba(239,68,68,0.10)", text: "#DC2626", border: "rgba(239,68,68,0.25)" },
  expired: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
};
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied to the
   certificate Active/Expiring Soon/Exhausted/Expired status badge. */
const STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";

/* ======================== COMPONENTS ======================== */

/* ----- Toast ----- */
function Toast({ message, onClose }) {
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const fadeT = setTimeout(() => setFading(true), 2700);
    const closeT = setTimeout(() => { onClose(); }, 3000);
    return () => { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(
    <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", animation: fading ? "fadeOut 0.3s ease forwards" : "fadeIn 0.3s" }}>
      {message}
    </div>,
    document.body
  );
}

/* ----- Field wrapper ----- */
function Field({ label, required, error, sub, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: error ? "var(--color-danger)" : "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
        {label}{required && <span style={{ color: "var(--color-danger)" }}>*</span>}
      </label>
      {children}
      {sub && <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{sub}</span>}
    </div>
  );
}

/* ----- Section divider ----- */
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid var(--color-border)" }}>{title}</div>
      {children}
    </div>
  );
}

/* ----- Alert Banner ----- */
function AlertBanner({ cert, t }) {
  const level = getAlertLevel(cert);
  if (!level) return null;
  const configs = {
    low: { bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.3)", color: "#D97706", icon: "⚠️", text: t.alertLow },
    critical: { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.3)", color: "#DC2626", icon: "🔴", text: t.alertCritical },
    exhausted: { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.4)", color: "#DC2626", icon: "🚫", text: t.alertExhausted },
    expired: { bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.3)", color: "#6B7280", icon: "⏰", text: t.alertExpired },
  };
  const c = configs[level];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, background: c.bg, border: `1px solid ${c.border}`, marginBottom: 14 }}>
      <span style={{ fontSize: 16 }}>{c.icon}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: c.color }}>{c.text}</span>
    </div>
  );
}

/* ----- Invoice Progress Bar ----- */
function InvoiceBar({ total, used }) {
  const avail = Math.max(0, total - used);
  const pct = total > 0 ? (used / total) * 100 : 0;
  let barColor = "var(--color-success)";
  if (avail <= 1) barColor = "var(--color-danger)";
  else if (avail <= 3) barColor = "var(--color-warning)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: "var(--color-border)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: barColor, transition: "width 0.3s" }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: avail <= 1 ? "var(--color-danger)" : avail <= 3 ? "var(--color-warning)" : "var(--color-text-secondary)", minWidth: 32, textAlign: "right" }}>
        {avail}/{total}
      </span>
    </div>
  );
}

/* ======================== DETAIL PANEL ======================== */
/* ═══════════════════════════════════════════════════════════════
   AUDIT REPORT PRINT
   Builds a self-contained HTML document with ALL CSS inline so the new
   window keeps the formatting. Mirrors the navy #1B3A4B Sales Report
   aesthetic. The assigned-invoices table is deduplicated by
   shipperInvoice — one shipper invoice can cover multiple shipments
   (lots), and showing it more than once would look like double usage.
   ═══════════════════════════════════════════════════════════════ */

function fmtDateLong(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}
function fmtDateShort(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

/* 2026-09-28 (Arturo: "necesito al visualizarlo para guardar pdf...
   que me permita ordenar ya sea por lote, por numero de factura o por
   fecha") — sort key is now a caller-supplied option instead of a
   fixed rule. Kept "invoice" (numeric-locale compare, so "TA-A 20040"
   sorts before "TA-A 20100" by the embedded number, not
   lexicographically) as the default — matches the ascending-by-
   invoice behavior Arturo asked for on 2026-09-14, still the initial
   selection in the picker below. Empty values always sink to the
   bottom regardless of which key is chosen. */
var AUDIT_SORT_KEYS = {
  invoice: function (a, b) {
    var aInv = a.shipperInvoice === "—" ? "" : a.shipperInvoice;
    var bInv = b.shipperInvoice === "—" ? "" : b.shipperInvoice;
    if (!aInv && !bInv) return 0;
    if (!aInv) return 1;
    if (!bInv) return -1;
    return String(aInv).localeCompare(String(bInv), undefined, { numeric: true, sensitivity: "base" });
  },
  lot: function (a, b) {
    var aLot = a.lot === "—" ? "" : a.lot;
    var bLot = b.lot === "—" ? "" : b.lot;
    if (!aLot && !bLot) return 0;
    if (!aLot) return 1;
    if (!bLot) return -1;
    return String(aLot).localeCompare(String(bLot), undefined, { numeric: true, sensitivity: "base" });
  },
  date: function (a, b) {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date < b.date ? -1 : (a.date > b.date ? 1 : 0);
  },
};

function buildAuditReportHTML(cert, t, sortBy) {
  /* Use the live-derived usageList when present (always set by the
     registry's resolvedCerts memo). Falls back to recomputing from
     gfi-shipments directly so this function still works if invoked with
     a bare cert from another module. */
  var rows = [];
  var liveList = cert.usageList;
  if (!liveList) {
    var freshShps = (function () { try { return JSON.parse(localStorage.getItem("gfi-shipments") || "[]"); } catch (e) { return []; } })();
    var freshBills = (function () { try { return JSON.parse(localStorage.getItem("gfi-bills") || "[]"); } catch (e) { return []; } })();
    liveList = computeCertUsage(cert, freshShps, freshBills);
  }
  liveList.forEach(function (u) {
    rows.push({ shipperInvoice: u.shipperInvoice || "—", lot: u.lot || "—", date: u.date || "" });
  });
  rows.sort(AUDIT_SORT_KEYS[sortBy] || AUDIT_SORT_KEYS.invoice);

  /* Arturo, 2026-09-16: "el reporte de auditoria del shipper... debe
     llevar el logotipo de ellos no el mio, ese reporte es de ellos
     para su auditor" — this document represents the SHIPPER's own
     export-certificate compliance for their auditor, not GFI's, so
     it carries the shipper's own logo (already uploaded via
     ShipperSettings.js, suppliers.logoUrl) instead of the hardcoded
     GFI LOGO_B64 used by every other GFI-branded report. No fallback
     to GFI's logo when the shipper hasn't uploaded one — falls back
     to their name as plain text instead, so GFI's own branding never
     appears on a document meant to be theirs. */
  var shipperLogoUrl = "";
  try {
    var sups = getSuppliers();
    var matchedSup = sups.find(function (s) { return String(s.id) === String(cert.supplierId); });
    shipperLogoUrl = (matchedSup && matchedSup.logoUrl) || "";
  } catch (e) { /* best-effort */ }

  var total = parseInt(cert.totalInvoices, 10) || 0;
  var used = rows.length;
  var available = Math.max(0, total - used);
  var pct = total > 0 ? Math.round((used / total) * 100) : 0;

  var navy = "#1B3A4B";
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + t.auditReportTitle + ' - ' + (cert.certNumber || "") + '</title><style>';
  h += '@page{size:letter;margin:0.6in 0.7in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:700px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid ' + navy + '}';
  /* Bumped to 100 — matches other GFI docs so "International" reads. */
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:22px;font-weight:300;color:' + navy + ';letter-spacing:2px;text-align:right;line-height:1.1}';
  h += '.doc-sub{font-size:11px;color:#666;text-align:right;margin-top:4px;font-family:"Courier New",monospace}';
  h += '.section-title{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:' + navy + ';font-weight:700;margin:18px 0 8px;padding-bottom:4px;border-bottom:2px solid ' + navy + '}';
  h += '.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 18px;margin:0 0 6px;padding:14px 18px;background:#f7f8fa;border-radius:6px;border-left:4px solid ' + navy + '}';
  h += '.meta-grid .label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  h += '.meta-grid .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.tracking{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:8px}';
  h += '.tracking .box{padding:14px;border-radius:8px;background:#f7f8fa;text-align:center;border:1px solid #e5e7eb}';
  h += '.tracking .num{font-family:"Courier New",monospace;font-size:22px;font-weight:800;color:' + navy + '}';
  h += '.tracking .num.danger{color:#DC2626}.tracking .num.warn{color:#D97706}.tracking .num.ok{color:#059669}';
  h += '.tracking .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-top:4px}';
  h += '.bar{margin:8px 0 4px;height:14px;background:#e5e7eb;border-radius:7px;overflow:hidden;position:relative}';
  h += '.bar-fill{height:100%;background:' + navy + ';border-radius:7px;transition:width 0.3s}';
  h += '.bar-label{display:flex;justify-content:space-between;font-size:9px;color:#666;margin-top:4px}';
  h += 'table{width:100%;border-collapse:collapse}';
  h += '.items th{padding:8px 10px;font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:' + navy + ';font-weight:600;text-align:left}';
  h += '.items th:first-child{border-radius:4px 0 0 0;width:36px;text-align:center}';
  h += '.items th:last-child{border-radius:0 4px 0 0;width:120px}';
  h += '.items td{padding:8px 10px;border-bottom:1px solid #eee;font-size:11px;vertical-align:middle}';
  h += '.items td:first-child{text-align:center;color:#999;font-size:10px}';
  h += '.items td.mono{font-family:"Courier New",monospace;font-weight:600}';
  h += '.items tr:nth-child(even){background:#fafbfc}';
  h += '.empty{padding:20px;text-align:center;color:#999;font-style:italic;background:#fafbfc;border-radius:4px}';
  h += '.footer{margin-top:24px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '.audit-note{margin-top:14px;padding:10px 14px;background:#fff7ed;border-left:3px solid #D97706;border-radius:4px;font-size:9px;color:#92400e;line-height:1.5}';
  h += '</style></head><body><div class="page">';

  /* Header */
  h += '<div class="header"><div class="logo">' + (shipperLogoUrl
    ? '<img src="' + shipperLogoUrl + '" />'
    : '<div style="font-size:16px;font-weight:700;color:' + navy + '">' + (cert.shipperName || "—") + '</div>') + '</div>';
  h += '<div><div class="doc-title">EXPORT CERTIFICATE<br/>AUDIT REPORT</div>';
  h += '<div class="doc-sub">' + (cert.certNumber || "—") + ' · ' + fmtDateLong(new Date().toISOString().substring(0, 10)) + '</div></div></div>';

  /* Certificate Information */
  h += '<div class="section-title">' + t.certificateInfo + '</div>';
  h += '<div class="meta-grid">';
  h += '<div><div class="label">Certificate #</div><div class="value">' + (cert.certNumber || "—") + '</div></div>';
  h += '<div><div class="label">Shipper</div><div class="value">' + (cert.shipperName || "—") + '</div></div>';
  h += '<div><div class="label">Product</div><div class="value">' + (cert.productName || "—") + '</div></div>';
  h += '<div><div class="label">' + t.certifyingBody + '</div><div class="value">' + (cert.certifierName || "—") + '</div></div>';
  h += '<div><div class="label">Period</div><div class="value">' + fmtDateLong(cert.periodFrom) + ' → ' + fmtDateLong(cert.periodTo) + '</div></div>';
  h += '<div><div class="label">' + t.importer + '</div><div class="value">Ground Fresh International, Inc.</div></div>';
  h += '</div>';

  /* Invoice Tracking */
  h += '<div class="section-title">Invoice Tracking</div>';
  h += '<div class="tracking">';
  h += '<div class="box"><div class="num">' + total + '</div><div class="lbl">Total Allowed</div></div>';
  h += '<div class="box"><div class="num">' + used + '</div><div class="lbl">Used</div></div>';
  var availClass = available <= 1 ? "danger" : available <= 3 ? "warn" : "ok";
  h += '<div class="box"><div class="num ' + availClass + '">' + available + '</div><div class="lbl">Available</div></div>';
  h += '</div>';
  /* Progress bar */
  h += '<div class="bar"><div class="bar-fill" style="width:' + pct + '%"></div></div>';
  h += '<div class="bar-label"><span>0</span><span><b>' + used + ' / ' + total + ' (' + pct + '%)</b></span><span>' + total + '</span></div>';

  /* Assigned Invoices Table */
  h += '<div class="section-title">Assigned Invoices</div>';
  if (rows.length === 0) {
    h += '<div class="empty">No invoices have been assigned to this certificate yet.</div>';
  } else {
    h += '<table class="items"><thead><tr><th>#</th><th>Shipper Invoice #</th><th>Lot(s)</th><th>Date</th></tr></thead><tbody>';
    rows.forEach(function (r, i) {
      h += '<tr><td>' + (i + 1) + '</td><td class="mono">' + (r.shipperInvoice || "—") + '</td><td class="mono">' + (r.lot || "—") + '</td><td>' + fmtDateShort(r.date) + '</td></tr>';
    });
    h += '</tbody></table>';
    h += '<div class="audit-note"><b>Note:</b> Each shipper invoice is listed once and counts as ONE use of the certificate. When the same invoice covers multiple shipments (lots), all lots are shown comma-separated in a single row.</div>';
  }

  /* 2026-09-28 (Arturo: "estos reportede de certificado de
     exportacion... remuevelo porque estos reportes no son mios, son
     para el uso del shipper") — same reasoning already applied to the
     report's logo (2026-09-16: shipper's own logo, never GFI's,
     "ese reporte es de ellos para su auditor") — GFI's own footer
     address/domain doesn't belong on a document meant to be theirs
     either. Removed entirely, no replacement footer added. */
  h += '</div></body></html>';
  return h;
}

/* 2026-09-28 (Arturo: "necesito al visualizarlo para guardar pdf
   (mediante descarga a carpeta del navegador)" -> "necesito poder
   enviar ese reporte al shipper y un preview del reporte en
   pantalla") — same direct-download building block already applied to
   growerAudit.js's PDF button (a real PDF via htmlToPdfBlob, no print
   dialog / folder picker), now reused by BOTH the on-screen preview
   (AuditReportPreviewModal, below) and the Send-by-Email attachment
   (EmailAuditReportModal) so all three surfaces are always the exact
   same document. */
function auditReportFilename(cert) {
  var name = "Export Certificate Audit Report — " + (cert.certNumber || "") + " — " + (cert.shipperName || "");
  return name.replace(/[\\/:*?"<>|]+/g, "").trim() + ".pdf";
}
function buildAuditReportBlob(cert, t, sortBy) {
  return htmlToPdfBlob(buildAuditReportHTML(cert, t, sortBy));
}

/* 2026-09-28 (Arturo: "necesito poder enviar ese reporte al shipper y
   un preview del reporte en pantalla") — mirrors
   SalesReports.js's EmailSalesReportModal/SalesReportPdfPreview
   pattern exactly (same recipient-resolution ladder, same
   attach-a-prebuilt-blob approach, same disclosure hook). Recipients
   come from the shipper's own contacts opted into the "compliance"
   category (SHIPPER_EMAIL_CATEGORIES, emailHelpers.js — added
   2026-09-28 for this exact purpose), falling back to the shipper's
   plain email when it has no contacts configured yet. */
function EmailAuditReportModal({ cert, t, lang, sortBy, prebuiltBlob, onClose, onSent, zIndex }) {
  var company = useMemo(function () { return loadCompanyProfile(); }, []);
  var sup = useMemo(function () {
    var sups = getSuppliers();
    return sups.find(function (s) { return String(s.id) === String(cert.supplierId); });
  }, [cert.supplierId]);
  var filename = auditReportFilename(cert);
  /* 2026-09-29 (Arturo: "el de send audit al shipper, el que yo hago
     manualmente tambien debe ser en español") — this is shipper-facing
     content (Mexican suppliers get Spanish by default per CLAUDE.md's
     COMMUNICATION rules, same reasoning already applied to
     export-cert-reminder-send.js's automated emails), so subject/body
     stay Spanish regardless of Arturo's own UI language toggle — only
     the composer's own title (further down) still follows `lang`,
     since that's UI chrome he sees, never sent to the shipper. */
  var subject = "Reporte de Auditoría — Certificado " + (cert.certNumber || "");

  var recipients = useMemo(function () {
    var contacts = filterContactsByCategory(sup && sup.contacts, "compliance");
    var list = contacts.length > 0
      ? contacts.map(function (c) { return { name: c.name || "", email: c.email }; })
      : (sup && sup.email ? [{ name: sup.shortName || sup.fullName || "", email: sup.email }] : []);
    return list.map(function (r) { return { name: r.name, email: r.email, checked: true }; });
  }, [sup]);

  var total = parseInt(cert.totalInvoices, 10) || 0;
  var used = usedOf(cert);
  var available = Math.max(0, total - used);
  /* Arturo, 2026-09-28: "diciendole cuantos le quedan" — the balance
     is already in the attached PDF's tracking boxes, but he wants it
     said plainly in the email text too, not just left for the shipper
     to notice inside the document. */
  /* Arturo, 2026-09-28: "falto Thanks / Ground-Fresh International" —
     the account's real signature is empty right now (CompanyProfile's
     Ground-Fresh entry has no HTML signature configured), so without
     an explicit sign-off the message just stopped short with no
     closing at all. Same plain wording as the automated low-balance/
     exhausted emails (export-cert-reminder-send.js) for consistency —
     if Arturo configures a real signature later, EmailDialog appends
     it separately below this, it doesn't replace it. */
  var defaultBody = "Saludos,\n\nAdjunto encontrará el Reporte de Auditoría del certificado de exportación " + (cert.certNumber || "—") + " para su revisión y registro.\n\nActualmente le quedan " + available + " de " + total + " facturas disponibles en este certificado.\n\nPor favor revise el documento adjunto y avísenos si tiene alguna pregunta.\n\nGracias\n\nGround-Fresh International";

  var buildAttachments = async function () {
    var blob = prebuiltBlob || (await buildAuditReportBlob(cert, t, sortBy));
    return [{ filename: filename, blob: blob, mimeType: "application/pdf" }];
  };

  /* 2026-09-29 (Arturo: "la ventana de envios de certificados de
     exportacion muestra en la parte superior monton de cuentas solo
     debe mostrar la GFI automated") — this window always sends the
     Audit Report to a shipper about their own certificate balance, so
     it should always go from the dedicated "GFI Automated" account
     (alerts@gfinexus.com), not offer a picker across every configured
     account (Ground-Fresh/Sweetleaf/GFI Automated). EmailDialog only
     renders the account-picker UI when it receives more than one
     account, so filtering down to just this one hides it. Falls back
     to the full account list if "GFI Automated" hasn't been created
     yet in Company Profile, so nothing breaks before that. */
  var allAccounts = company.emailAccounts || [];
  var automatedAcct = allAccounts.find(function (a) { return a && String(a.name || "").toLowerCase().indexOf("automat") >= 0; });
  var scopedAccounts = automatedAcct ? [automatedAcct] : allAccounts;

  /* 2026-09-29 real bug (Arturo: "porque agregaste mi firma por
     default al envio de alertas de certificados de exportacion?") —
     the scoping above only fixed the picker UI. At actual send time,
     EmailDialog.js's buildRenderedHtml() re-fetches the FULL,
     unscoped company_profile.emailAccounts from Supabase (by design,
     for freshness — see that file) and resolves the active account by
     matching `.email`, not by identity. "GFI Automated" and
     "Ground-Fresh" both use the SAME real `.email`
     (a.gonzalez@ground-fresh.com — the real inbox replies should land
     in), so that match silently picked whichever entry sits first in
     the array — "Ground-Fresh" — not "GFI Automated". Two real
     consequences: (1) the actual From sent was Ground-Fresh's masked
     alias, not alerts@gfinexus.com, defeating the whole point of this
     account; (2) Ground-Fresh's own account signature (if Arturo has
     one configured there) got force-applied, which is the personal
     signature he's reporting. `refreshAccountsBeforeSend` lets a
     caller override EmailDialog's default fresh-fetch entirely — used
     here to hand back ONLY the scoped array, so the match can never
     be ambiguous regardless of what email address any account shares. */
  var refreshScopedAccount = async function () {
    if (!automatedAcct) return scopedAccounts;
    try {
      var rows = await loadFromSupabase("company_profile", { force: true });
      var fresh = (rows || [])[0];
      var freshList = (fresh && Array.isArray(fresh.emailAccounts)) ? fresh.emailAccounts : allAccounts;
      var freshAutomated = freshList.find(function (a) { return a && String(a.name || "").toLowerCase().indexOf("automat") >= 0; });
      return freshAutomated ? [freshAutomated] : scopedAccounts;
    } catch (e) { return scopedAccounts; }
  };

  return (
    <EmailDialog
      lang={lang}
      title={(lang === "es" ? "Enviar Reporte de Auditoría" : "Send Audit Report") + " — " + (cert.certNumber || "")}
      recipients={recipients}
      docOptions={[]}
      defaultSubject={subject}
      defaultBody={defaultBody}
      fromEmail={(automatedAcct && automatedAcct.email) || company.outboundEmail || ""}
      emailAccounts={scopedAccounts}
      refreshAccountsBeforeSend={refreshScopedAccount}
      /* No signature picker for this account on purpose — "GFI
         Automated" is meant to be impersonal; the plain "Thank
         you / Ground-Fresh International" sign-off already baked
         into defaultBody above covers it. Passing company's full
         emailSignatures list here would auto-select whichever one is
         flagged isDefault (Arturo's own, with his name) — exactly
         what he doesn't want on this send. */
      signatures={[]}
      alwaysBuildAttachments={true}
      buildAttachments={buildAttachments}
      /* This module never sends to Four Seasons/SweetLeaf (its
         recipients are always the exporting shipper), so the SL
         branch of appendDisclosureByAccount can never legitimately
         trigger here — no slSettings lookup needed, unlike
         SalesReports.js/InvoiceRegistry.js which do send to both
         brands from the same dialog. */
      postProcessHtmlBody={function (html, resolvedFrom) { return appendDisclosureByAccount(html, resolvedFrom, company, null); }}
      onClose={onClose}
      onSent={onSent}
      zIndex={zIndex}
    />
  );
}

/* PDF preview modal — same 3-button contract as
   SalesReports.js's SalesReportPdfPreview (Download / Send by Email /
   Close), generating the blob ONCE and reusing it for both actions. */
function AuditReportPreviewModal({ cert, t, lang, sortBy, onClose }) {
  var [blob, setBlob] = useState(null);
  var [blobUrl, setBlobUrl] = useState(null);
  var [error, setError] = useState(null);
  var [showEmailModal, setShowEmailModal] = useState(false);
  var filename = auditReportFilename(cert);

  useEffect(function () {
    var cancelled = false;
    var createdUrl = null;
    setBlob(null); setBlobUrl(null); setError(null);
    try {
      buildAuditReportBlob(cert, t, sortBy).then(function (b) {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(b);
        setBlob(b);
        setBlobUrl(createdUrl);
      }).catch(function (e) {
        if (!cancelled) setError(e && e.message ? e.message : String(e));
      });
    } catch (syncErr) {
      setError(syncErr && syncErr.message ? syncErr.message : String(syncErr));
    }
    return function () {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cert && cert.id, sortBy]);

  var handleDownload = function () {
    if (!blob) return;
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  var btn = { padding: "6px 16px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 };
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 10002, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: "90vw", height: "90vh", background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "12px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{(lang === "es" ? "Vista previa — Reporte de Auditoría" : "Audit Report Preview") + " — " + (cert.certNumber || "")}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleDownload} disabled={!blob} style={{ ...btn, border: "none", background: blob ? "#1B3A4B" : "var(--color-border)", color: blob ? "#fff" : "var(--color-text-muted)", cursor: blob ? "pointer" : "not-allowed" }}>{lang === "es" ? "Descargar" : "Download"}</button>
            <button onClick={function () { setShowEmailModal(true); }} disabled={!blob} style={{ ...btn, border: "1px solid #1B3A4B", background: "transparent", color: blob ? "#1B3A4B" : "var(--color-text-muted)", cursor: blob ? "pointer" : "not-allowed" }}>{lang === "es" ? "Enviar por email" : "Send by Email"}</button>
            <button onClick={onClose} style={{ ...btn, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)" }}>{lang === "es" ? "Cerrar" : "Close"}</button>
          </div>
        </div>
        <div style={{ flex: 1, background: "#525659", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {error && <div style={{ color: "#fff", fontSize: 13, padding: 20, textAlign: "center" }}>{(lang === "es" ? "Error al generar el PDF: " : "Could not generate PDF: ") + error}</div>}
          {!error && !blobUrl && <div style={{ color: "#fff", fontSize: 13 }}>{lang === "es" ? "Generando PDF…" : "Generating PDF…"}</div>}
          {blobUrl && <iframe title="audit-report-pdf-preview" src={blobUrl} style={{ width: "100%", height: "100%", border: "none" }} />}
        </div>
      </div>
      {showEmailModal && blob && (
        <EmailAuditReportModal
          cert={cert}
          t={t}
          lang={lang}
          sortBy={sortBy}
          prebuiltBlob={blob}
          onClose={function () { setShowEmailModal(false); }}
          zIndex={10004}
        />
      )}
    </div>,
    document.body
  );
}

function DetailPanel({ cert, t, lang, onEdit, onClose, canEdit = true }) {
  /* Derived count/list from gfi-shipments — see resolvedCerts in the
     registry. Falls back to legacy usedInvoices.length only if the parent
     forgot to inject. */
  const used = usedOf(cert);
  const usageList = useMemo(() => cert.usageList || [], [cert.usageList]);
  const avail = (cert.totalInvoices || 0) - used;
  const status = getStatus(cert);
  const ss = STATUS_STYLES[status] || STATUS_STYLES.active;
  const statusLabel = t[status] || status;
  /* 2026-09-28 (Arturo: "necesito al visualizarlo para guardar pdf...
     que me permita ordenar ya sea por lote, por numero de factura o
     por fecha" -> "pero porque no los ordena en pantalla?") — picked
     once here, feeds BOTH the PDF download and the on-screen "Assigned
     Invoices" table below (sortedUsageList), so what's on screen
     always matches what downloads. */
  const [auditSortBy, setAuditSortBy] = useState("invoice");
  /* 2026-09-28 (Arturo: "necesito poder enviar ese reporte al shipper
     y un preview del reporte en pantalla") — the button now opens the
     same 3-button preview modal (Download / Send by Email / Close)
     already used elsewhere in the app (SalesReportPdfPreview), instead
     of downloading straight away — the direct-download helper
     (downloadAuditReportPdf) is reused INSIDE that modal, not called
     from here anymore. */
  const [showAuditPreview, setShowAuditPreview] = useState(false);
  const sortedUsageList = useMemo(() => {
    var list = usageList.slice();
    list.sort(AUDIT_SORT_KEYS[auditSortBy] || AUDIT_SORT_KEYS.invoice);
    return list;
  }, [usageList, auditSortBy]);

  var title = (cert.certNumber || "—") + "  ·  " + (cert.shipperName || "—") + " · " + (cert.productName || "—");

  return (
    <DraggableModal title={title} onClose={onClose}>
      {/* Sub-header: status badge + actions */}
      <div style={{ padding: "10px 24px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(0,0,0,0.015)", flexShrink: 0, flexWrap: "wrap", gap: 8 }}>
        <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: ss.bg, color: ss.text, border: `1px solid ${ss.border}`, boxShadow: STATUS_BEVEL }}>{statusLabel}</span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600 }}>{t.auditSortBy}</span>
          <SearchSelect
            value={auditSortBy}
            onChange={setAuditSortBy}
            options={[
              { value: "invoice", label: t.auditSortInvoice },
              { value: "lot", label: t.auditSortLot },
              { value: "date", label: t.auditSortDate },
            ]}
            style={{ minWidth: 110 }}
          />
          <button onClick={() => setShowAuditPreview(true)} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>📄 {t.printAudit}</button>
          {canEdit && <button onClick={() => onEdit(cert)} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.editBtn}</button>}
        </div>
      </div>
      {showAuditPreview && (
        <AuditReportPreviewModal
          cert={cert}
          t={t}
          lang={lang}
          sortBy={auditSortBy}
          onClose={() => setShowAuditPreview(false)}
        />
      )}

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", minHeight: 0 }}>
          <AlertBanner cert={cert} t={t} />

          <Section title={t.identity}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.certNumber}</div><div style={{ fontSize: 13, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{cert.certNumber || "—"}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.shipper}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{cert.shipperName || "—"}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.product}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{cert.productName || "—"}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.certifier}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{cert.certifierName || "—"}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.importer}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{t.importerFixed}</div></div>
              <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.period}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(cert.periodFrom)} → {fmtDate(cert.periodTo)}</div></div>
            </div>
          </Section>

          <Section title={t.invoiceTracking}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div style={{ padding: 14, borderRadius: 10, background: "var(--color-bg)", textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text)" }}>{cert.totalInvoices || 0}</div>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 2 }}>{t.totalInvoices}</div>
              </div>
              <div style={{ padding: 14, borderRadius: 10, background: "var(--color-bg)", textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" }}>{used}</div>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 2 }}>{t.used}</div>
              </div>
              <div style={{ padding: 14, borderRadius: 10, background: avail <= 1 ? "rgba(239,68,68,0.06)" : avail <= 3 ? "rgba(245,158,11,0.06)" : "var(--color-bg)", textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: avail <= 1 ? "var(--color-danger)" : avail <= 3 ? "var(--color-warning)" : "var(--color-success)" }}>{avail}</div>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 2 }}>{t.available}</div>
              </div>
            </div>
            <InvoiceBar total={cert.totalInvoices || 0} used={used} />
          </Section>

          {/* Used Invoices List — driven by the live derived list, so
              shipments deleted or reassigned in ShipmentRegistry no
              longer appear here. */}
          {usageList.length > 0 && (
            <Section title={t.invoiceList}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 10 }}>#</th>
                    <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 10 }}>{t.shipperInvoice}</th>
                    <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 10 }}>{t.shipmentLot}</th>
                    <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 10 }}>{t.assignDate}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsageList.map((entry, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "6px 8px", color: "var(--color-text-muted)" }}>{i + 1}</td>
                      <td style={{ padding: "6px 8px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{entry.shipperInvoice || "—"}</td>
                      <td style={{ padding: "6px 8px" }}>{entry.lot || "—"}</td>
                      <td style={{ padding: "6px 8px", color: "var(--color-text-muted)" }}>{fmtDate(entry.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* Document */}
          {cert.document && (
            <Section title={t.document}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: "var(--color-bg)", border: "1px solid var(--color-border)" }}>
                <span style={{ fontSize: 20 }}>📄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text)" }}>{cert.document.name}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{cert.document.size}</div>
                </div>
                <button onClick={() => {
                  const a = document.createElement("a"); a.href = cert.document.data; a.download = cert.document.name; a.click();
                }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.viewDoc}</button>
              </div>
            </Section>
          )}

        {cert.notes && (
          <Section title={t.notes}>
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{cert.notes}</div>
          </Section>
        )}
      </div>
    </DraggableModal>
  );
}

/* ======================== FORM ======================== */
function CertForm({ cert, certs, t, lang, onSave, onDelete, onCancel, usedCount, lockSupplierId, canDelete = true }) {
  /* usedCount is the live derived count (from gfi-shipments) — passed in
     by the registry so the form's "Available" display matches the list/
     detail views. Falls back to the legacy stored array length. */
  var liveUsed = typeof usedCount === "number" ? usedCount : (cert && (cert.usedInvoices || []).length) || 0;
  const isNew = !cert;
  /* When lockSupplierId is set (Shipper Portal), seed a fresh form
     with that supplier pre-selected. The picker further down is also
     disabled below so the shipper can't repoint it. */
  const _initial = (function () {
    if (cert) return { ...cert };
    var base = {
      id: "", certNumber: "", supplierId: "", shipperName: "",
      productName: "", productSource: "shipper", certifierName: "",
      periodFrom: "", periodTo: "", totalInvoices: 16,
      usedInvoices: [], document: null, notes: "",
    };
    if (lockSupplierId) {
      base.supplierId = lockSupplierId;
      try {
        var sup = getSuppliers().find(function (s) { return String(s.id) === String(lockSupplierId); });
        if (sup) {
          base.shipperName = sup.shortName || sup.fullName || sup.name || sup.companyName || "";
          var orgCert = getShipperOrgCert(lockSupplierId);
          if (orgCert) base.certifierName = orgCert.certifier || orgCert.certifierOther || "";
        }
      } catch (e) { /* ignore — defaults stay empty */ }
    }
    return base;
  })();
  const [f, setF] = useState(_initial);
  const [errors, setErrors] = useState({});
  const [showDel, setShowDel] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const suppliers = useMemo(() => getSuppliers(), []);
  const allOrgProducts = useMemo(() => getProducts(), []);

  // When shipper changes, auto-fill certifier and get certified products
  const shipperCertProducts = useMemo(() => {
    if (!f.supplierId) return [];
    return getShipperCertifiedProducts(f.supplierId);
  }, [f.supplierId]);

  const shipperCertifier = useMemo(() => {
    if (!f.supplierId) return "";
    const orgCert = getShipperOrgCert(f.supplierId);
    return orgCert ? (orgCert.certifier || orgCert.certifierOther || "") : "";
  }, [f.supplierId]);

  const set = (k, v) => { setF(prev => ({ ...prev, [k]: v })); setDirty(true); setErrors(prev => ({ ...prev, [k]: false })); };

  const handleShipperChange = (supplierId) => {
    const sup = suppliers.find(s => String(s.id) === String(supplierId));
    var supName = "";
    if (sup) {
      supName = sup.shortName || sup.fullName || sup.name || sup.companyName || "";
      if (!supName && (sup.ownerNombre || sup.nombre)) supName = [sup.ownerNombre || sup.nombre, sup.ownerApPaterno || sup.apPaterno, sup.ownerApMaterno || sup.apMaterno].filter(Boolean).join(" ");
    }
    setF(prev => ({
      ...prev,
      supplierId,
      shipperName: supName,
      certifierName: "",
      productName: "",
      productSource: "shipper",
    }));
    setDirty(true);
    // Auto-fill certifier after state update
    setTimeout(() => {
      const orgCert = getShipperOrgCert(supplierId);
      if (orgCert) {
        setF(prev => ({ ...prev, certifierName: orgCert.certifier || orgCert.certifierOther || "" }));
      }
    }, 0);
  };

  const processFile = (files) => {
    const file = (files && files[0]) || null;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      set("document", { name: file.name, size: `${(file.size / 1024).toFixed(1)} KB`, data: ev.target.result, type: file.type });
    };
    reader.readAsDataURL(file);
  };
  const handleFileUpload = (e) => { processFile(e.target.files); };

  /* Defensive string coercion — Supabase returns null for empty text
     columns. S() prevents "Cannot read properties of null (reading
     'trim')" when editing a cert imported via the migration. */
  const S = v => (v == null ? "" : String(v));
  const validate = () => {
    const errs = {};
    if (!S(f.certNumber).trim()) errs.certNumber = true;
    if (!f.supplierId) errs.supplierId = true;
    if (!f.productName) errs.productName = true;
    if (!f.periodFrom) errs.periodFrom = true;
    if (!f.periodTo) errs.periodTo = true;
    if (!f.totalInvoices || f.totalInvoices < 1) errs.totalInvoices = true;
    // Dup check
    if (S(f.certNumber).trim() && certs.some(c => c.id !== f.id && S(c.certNumber) === S(f.certNumber).trim())) errs.dupCert = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const toSave = {
      ...f,
      id: f.id || nextId(certs),
      certNumber: S(f.certNumber).trim(),
      certifierName: f.certifierName || shipperCertifier,
    };
    onSave(toSave);
  };

  const tryCancel = () => {
    if (dirty) { setShowExitConfirm(true); } else { onCancel(); }
  };

  // Product options — shipper certified products first, then "other" with all organic products
  const productOptions = useMemo(() => {
    /* In shipperMode the picker is restricted to the 3 canonical
       commodities (Blackberry / Blueberry / Raspberry) and we drop
       the "other" bucket entirely. Without this, products with
       slightly different capitalization / wording from the products
       catalog leaked in as duplicates of the same commodity. */
    if (lockSupplierId) {
      const canonical = ["Blackberry", "Blueberry", "Raspberry"];
      const certified = (shipperCertProducts || []).map(p => String(p));
      const inCert = canonical.filter(c => certified.some(cp => cp.toLowerCase() === c.toLowerCase()));
      const remaining = canonical.filter(c => !inCert.includes(c));
      return { shipper: inCert.length > 0 ? inCert : canonical, other: inCert.length > 0 ? remaining : [] };
    }
    const shipperProds = shipperCertProducts;
    const otherProds = allOrgProducts
      .map(p => p.product || p.productName || "")
      .filter(Boolean)
      .filter(p => !shipperProds.includes(p));
    // Deduplicate
    return {
      shipper: [...new Set(shipperProds)],
      other: [...new Set(otherProds)],
    };
  }, [shipperCertProducts, allOrgProducts, lockSupplierId]);

  return (
    <DraggableModal title={isNew ? t.add : t.edit} onClose={tryCancel}>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {errors.dupCert && <div style={{ padding: "8px 12px", borderRadius: 6, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "var(--color-danger)", fontSize: 12, marginBottom: 14 }}>{t.dupCert}</div>}
          {errors.fillRequired && <div style={{ padding: "8px 12px", borderRadius: 6, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "var(--color-danger)", fontSize: 12, marginBottom: 14 }}>{t.fillRequired}</div>}

          <Section title={t.identity}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label={t.certNumber} required error={errors.certNumber}>
                  <input value={f.certNumber} onChange={e => set("certNumber", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="840-XXXXXXXXXX-XXXXXX" />
                </Field>
              </div>
              <Field label={t.shipper} required error={errors.supplierId}>
                <SearchSelect value={f.supplierId} onChange={handleShipperChange} disabled={!!lockSupplierId} placeholder={t.selectShipper} options={suppliers.map(s => ({ value: s.id, label: s.shortName || s.fullName }))} />
              </Field>
              <Field label={t.importer}>
                <input value={t.importerFixed} disabled style={{ ...inp, background: "var(--color-bg)", color: "var(--color-text-muted)" }} />
              </Field>

              {/* Product selector */}
              <div style={{ gridColumn: "1 / -1" }}>
                <Field label={t.product} required error={errors.productName}>
                  {f.supplierId && productOptions.shipper.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <SearchSelect
                        value={f.productSource === "shipper" ? f.productName : ""}
                        onChange={v => { set("productName", v); set("productSource", "shipper"); }}
                        placeholder={"— " + t.shipperProducts + " —"}
                        options={productOptions.shipper.map(p => ({ value: p, label: p }))}
                      />
                      {productOptions.other.length > 0 && (
                        <SearchSelect
                          value={f.productSource === "other" ? f.productName : ""}
                          onChange={v => { set("productName", v); set("productSource", "other"); }}
                          placeholder={"— " + t.otherProducts + " —"}
                          options={productOptions.other.map(p => ({ value: p, label: p }))}
                        />
                      )}
                    </div>
                  ) : (
                    <SearchSelect value={f.productName} onChange={v => { set("productName", v); set("productSource", "other"); }} placeholder={t.selectProduct} options={allOrgProducts.map(p => ({ value: p.product || p.productName, label: p.product || p.productName }))} />
                  )}
                </Field>
              </div>

              <Field label={t.certifier} sub={shipperCertifier ? (lang === "es" ? "Auto-llenado desde certificación orgánica del proveedor" : "Auto-filled from shipper's organic certification") : (lang === "es" ? "Selecciona un proveedor con certificación orgánica" : "Select a shipper with organic certification")}>
                {shipperCertifier ? (
                  <div style={{ ...inp, background: "var(--color-accent-bg)", border: "1px solid var(--color-accent)", color: "var(--color-accent)", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13 }}>✓</span> {shipperCertifier}
                  </div>
                ) : (
                  <input value={f.certifierName || ""} onChange={e => set("certifierName", e.target.value)} style={inp} placeholder={lang === "es" ? "Sin certificación orgánica encontrada" : "No organic certification found"} />
                )}
              </Field>
            </div>
          </Section>

          <Section title={t.period}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label={t.periodFrom} required error={errors.periodFrom}>
                <input type="date" value={f.periodFrom} onChange={e => set("periodFrom", e.target.value)} style={inp} />
              </Field>
              <Field label={t.periodTo} required error={errors.periodTo}>
                <input type="date" value={f.periodTo} onChange={e => set("periodTo", e.target.value)} style={inp} />
              </Field>
            </div>
          </Section>

          <Section title={t.invoiceTracking}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label={t.totalContainers} required error={errors.totalInvoices}>
                <input type="number" min="1" value={f.totalInvoices} onChange={e => set("totalInvoices", parseInt(e.target.value) || 0)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
              </Field>
              <Field label={t.available}>
                <div style={{ ...inp, background: "var(--color-bg)", display: "flex", alignItems: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                  {Math.max(0, (f.totalInvoices || 0) - liveUsed)}
                </div>
              </Field>
            </div>
            {!isNew && liveUsed > 0 && (
              <div style={{ marginTop: 12 }}>
                <InvoiceBar total={f.totalInvoices || 0} used={liveUsed} />
              </div>
            )}
          </Section>

          <Section title={t.document}>
            {f.document ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: "var(--color-bg)", border: "1px solid var(--color-border)" }}>
                <span style={{ fontSize: 18 }}>📄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{f.document.name}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{f.document.size}</div>
                </div>
                <button onClick={() => set("document", null)} style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-danger)", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{t.removeDoc}</button>
              </div>
            ) : (
              <FileDropZone onFiles={processFile} multiple={false}>
                <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "16px 12px", borderRadius: 8, border: "2px dashed var(--color-border)", cursor: "pointer", color: "var(--color-text-muted)", fontSize: 12, transition: "all 0.2s" }}>
                  <span style={{ fontSize: 18 }}>📎</span>
                  {t.uploadDoc}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} style={{ display: "none" }} />
                </label>
              </FileDropZone>
            )}
          </Section>

          <Section title={t.notes}>
            <textarea value={f.notes || ""} onChange={e => set("notes", e.target.value)} rows={3} style={{ ...inp, resize: "vertical" }} />
          </Section>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            {!isNew && !showDel && canDelete && <button onClick={() => setShowDel(true)} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "var(--color-danger)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.delete}</button>}
            {showDel && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "var(--color-danger)" }}>{t.confirmDel}</span>
                <button onClick={() => onDelete(cert.id)} style={{ padding: "4px 10px", borderRadius: 4, border: "none", background: "var(--color-danger)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.yes}</button>
                <button onClick={() => setShowDel(false)} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.no}</button>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={tryCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.cancel}</button>
            <button onClick={handleSave} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.save}</button>
          </div>
        </div>

        {/* Exit confirm */}
        {showExitConfirm && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
            <div style={{ background: "var(--color-bg-card-solid)", padding: 24, borderRadius: 12, maxWidth: 340, textAlign: "center" }}>
              <p style={{ fontSize: 13, marginBottom: 16, color: "var(--color-text)" }}>{t.confirmExit}</p>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button onClick={onCancel} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--color-danger)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yesExit}</button>
                <button onClick={() => setShowExitConfirm(false)} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12 }}>{t.noExit}</button>
              </div>
            </div>
          </div>
        )}
    </DraggableModal>
  );
}

/* ======================== MAIN ======================== */
/* `lockSupplierId` (optional) renders the registry inside the Shipper
   Portal. When set:
     * the cert list is pre-filtered to that supplier only
     * the shipper-filter dropdown in the toolbar is hidden
     * the supplier picker in CertForm is locked + pre-populated
   It receives the LEGACY supplier id (the same string the records use
   in their `supplierId` field). When falsy the registry is identical
   to the original admin view. */
export default function ExportCertificateRegistry({ lang: globalLang, lockSupplierId, canCreate = true, canEdit = true, canDelete = true }) {
  const lang = globalLang || "es";
  const t = T[lang];
  const [certs, setCerts] = useState([]);
  const [search, setSearch] = useState("");
  const [filterShipper, setFilterShipper] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  /* Season filter — multi-select grouped per CLAUDE.md "REGLA DE FILTRO
     DE SEASON". Pre-selects all Open seasons on mount. Season is derived
     from `cert.periodFrom` (the cert's validity start). NOTE: a cert
     whose validity straddles two seasons is classified by its start —
     this is a simplification but periodFrom is the only single date
     anchor available. */
  const [filterSeasons, setFilterSeasons] = useState(() => defaultOpenSeasonIds());
  const [showExpired, setShowExpired] = useState(true);
  const [dateFrom, setDateFrom] = useState(""); const [dateTo, setDateTo] = useState("");
  /* Local season-name helper based on cert.periodFrom. */
  const certSeasons = useMemo(() => loadLS("gfi-seasons", []), []);
  const seasonNameForCert = useCallback((periodFrom) => {
    if (!periodFrom) return "";
    const d = new Date(periodFrom + "T00:00:00");
    const s = certSeasons.find(s => new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"));
    return s ? (s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)) : "";
  }, [certSeasons]);
  const [sortCol, setSortCol] = useState(() => { try { const s = JSON.parse(localStorage.getItem("gfi-export-certs-sort") || "null"); return (s && s.column) || "periodTo"; } catch { return "periodTo"; } });
  const [sortDir, setSortDir] = useState(() => { try { const s = JSON.parse(localStorage.getItem("gfi-export-certs-sort") || "null"); return (s && s.direction) || "desc"; } catch { return "desc"; } });
  useEffect(() => { try { localStorage.setItem("gfi-export-certs-sort", JSON.stringify({ column: sortCol, direction: sortDir })); } catch {} }, [sortCol, sortDir]);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const aliveRef = useRef(true);

  /* Source of truth = Supabase `export_certs`. Dual-write to localStorage
     on every change so the cert filter dropdowns in ShipmentRegistry /
     BillRegistry / ShipperPortal (which still read loadLS('gfi-export-certs'))
     stay in sync. Drop the dual write once those callers migrate. */
  const writeCertsCache = (next) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  };
  const reload = async () => {
    setError(null); setLoading(true);
    try {
      const rows = await loadFromSupabase("export_certs", { force: true });
      if (aliveRef.current) { setCerts(rows); writeCertsCache(rows); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load export certs: " + (e.message || String(e)));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };
  useEffect(() => {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("export_certs")
      .then(rows => { if (aliveRef.current) { setCerts(rows); writeCertsCache(rows); } })
      .catch(e => { if (aliveRef.current) setError("Could not load export certs: " + (e.message || String(e))); })
      .finally(() => { if (aliveRef.current) setLoading(false); });
    return () => { aliveRef.current = false; };
  }, []);

  /* 2026-09-30 (Arturo: "al seleccionar Gomez Berries no me importa su
     certificadora") — getShipperOrgCert/getShipperCertifiedProducts
     (used by CertForm's auto-fill) both read `suppliers`/`certifications`
     via plain loadLS(), never a fresh Supabase fetch. If this browser
     tab never opened Suppliers or Certifications first, those local
     mirrors can be stale or empty and the auto-fill silently finds
     nothing — same recurring "localStorage isn't fresh" bug class
     documented elsewhere in this app. Force-refresh both once when this
     registry mounts so CertForm's synchronous localStorage reads always
     have real data, regardless of what the user opened before this. */
  useEffect(() => {
    loadFromSupabase("suppliers").catch(() => {});
    loadFromSupabase("certifications").catch(() => {});
  }, []);

  /* ONE-TIME DATA RESTORATION
     Cert 840-1560012870-250008 picked up 3 phantom entries in its
     usedInvoices array (lots 57915 / 57916 / 57917, dated 2026-05-11,
     with blank or "—" shipperInvoice). Drop those entries so the live
     count returns to 9/12 (3 Available). Guarded by a localStorage
     flag so the body short-circuits after the first successful run. */
  useEffect(() => {
    var FLAG = "gfi-mig-cert-250008-phantoms-v1";
    try { if (localStorage.getItem(FLAG)) return; } catch (e) { return; }
    var list = loadLS(STORAGE_KEY, []);
    if (!Array.isArray(list) || list.length === 0) return;
    var phantomLots = { "57915": true, "57916": true, "57917": true };
    var changed = false;
    var next = list.map(function (c) {
      if (c.certNumber !== "840-1560012870-250008") return c;
      var ui = c.usedInvoices || [];
      var cleaned = ui.filter(function (u) {
        var lotKey = String(u && u.lot || "");
        var inv = String(u && u.shipperInvoice || "").trim();
        var emptyInvoice = inv === "" || inv === "—";
        if (phantomLots[lotKey] && emptyInvoice) return false;
        return true;
      });
      if (cleaned.length === ui.length) return c;
      changed = true;
      return { ...c, usedInvoices: cleaned };
    });
    if (changed) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); setCerts(next); } catch (e) { return; }
    }
    try { localStorage.setItem(FLAG, "1"); } catch (e) {}
  }, []);

  /* Force a re-derivation of usedCount when the user returns from another
     module (e.g. ShipmentRegistry where they may have deleted/reassigned
     a shipment that referenced a cert). Bumping shipmentsRev invalidates
     the allShipments memo. */
  const [shipmentsRev, setShipmentsRev] = useState(0);
  useEffect(() => {
    var refresh = function () { setShipmentsRev(function (n) { return n + 1; }); };
    window.addEventListener("focus", refresh);
    return function () { window.removeEventListener("focus", refresh); };
  }, []);
  const allShipments = useMemo(() => loadLS("gfi-shipments", []), [shipmentsRev, certs]);
  /* Bills are also scanned for cert references. When the user imported
     Bills without their parent Shipments (or before importing them),
     the cert link lives on the bill lines only — `computeCertUsage`
     unions them with shipments and dedupes by shipperInvoice. */
  const allBills = useMemo(() => loadLS("gfi-bills", []), [shipmentsRev, certs]);
  const handleSave = async (c) => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      const saved = await saveToSupabase("export_certs", c);
      const exists = certs.find(x => x.id === c.id);
      const next = exists ? certs.map(x => x.id === c.id ? saved : x) : certs.concat([saved]);
      setCerts(next); writeCertsCache(next);
      setShowForm(false); setEditing(null); setViewing(null);
      setToast(t.saved);
    } catch (e) {
      setError("Could not save export cert: " + (e.message || String(e)));
      setToast("\u26a0 " + e.message);
    } finally { setSaving(false); }
  };
  const handleDelete = async (id) => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await deleteFromSupabase("export_certs", id);
      const next = certs.filter(x => x.id !== id);
      setCerts(next); writeCertsCache(next);
      setShowForm(false); setEditing(null); setViewing(null);
      setToast(t.deleted);
    } catch (e) {
      setError("Could not delete export cert: " + (e.message || String(e)));
      setToast("\u26a0 " + e.message);
    } finally { setSaving(false); }
  };
  const handleEdit = (c) => { setViewing(null); setEditing(c); setShowForm(true); };

  /* Resolve shipper names from registry for certs that may not have them.
     Also injects usedCount + usageList (live-derived from gfi-shipments)
     so every downstream consumer (sort, list view, detail panel, audit
     report, edit form) reads a consistent, current count. */
  const resolvedCerts = useMemo(() => {
    const sups = getSuppliers();
    return certs.map(c => {
      var usageList = computeCertUsage(c, allShipments, allBills);
      var withUsage = { ...c, usedCount: usageList.length, usageList: usageList };
      if (withUsage.shipperName) return withUsage;
      if (!withUsage.supplierId) return withUsage;
      var sup = sups.find(x => String(x.id) === String(withUsage.supplierId));
      if (!sup) return withUsage;
      var name = sup.shortName || sup.fullName || sup.name || sup.companyName || "";
      if (!name && (sup.ownerNombre || sup.nombre)) name = [sup.ownerNombre || sup.nombre, sup.ownerApPaterno || sup.apPaterno, sup.ownerApMaterno || sup.apMaterno].filter(Boolean).join(" ");
      return name ? { ...withUsage, shipperName: name } : withUsage;
    });
  }, [certs, allShipments, allBills]);

  // Unique shippers and products for filters
  const uniqueShippers = useMemo(() => [...new Set(resolvedCerts.map(c => c.shipperName).filter(Boolean))].sort(), [resolvedCerts]);
  const uniqueProducts = useMemo(() => [...new Set(resolvedCerts.map(c => c.productName).filter(Boolean))].sort(), [resolvedCerts]);

  // Filter & sort
  const filtered = useMemo(() => {
    let list = resolvedCerts;
    /* lockSupplierId is the outer gate when rendered inside the
       Shipper Portal — every other filter runs against this subset. */
    if (lockSupplierId) list = list.filter(c => String(c.supplierId) === String(lockSupplierId));
    if (!showExpired) list = list.filter(c => getStatus(c) !== "expired");
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(c =>
        (c.certNumber || "").toLowerCase().includes(s) ||
        (c.shipperName || "").toLowerCase().includes(s) ||
        (c.productName || "").toLowerCase().includes(s)
      );
    }
    if (filterShipper) list = list.filter(c => c.shipperName === filterShipper);
    if (filterProduct) list = list.filter(c => c.productName === filterProduct);
    if (filterStatus) list = list.filter(c => getStatus(c) === filterStatus);
    if (filterSeasons.length > 0) list = list.filter(c => filterSeasons.indexOf(resolveSeasonId(certSeasons, c.periodFrom, c.seasonId)) >= 0);
    if (dateFrom || dateTo) list = list.filter(c => { const d = c.periodFrom || ""; if (!d) return false; if (dateFrom && d < dateFrom) return false; if (dateTo && d > dateTo) return false; return true; });
    return list;
  }, [resolvedCerts, search, filterShipper, filterProduct, filterStatus, filterSeasons, showExpired, dateFrom, dateTo, certSeasons, lockSupplierId]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case "certNumber": va = a.certNumber || ""; vb = b.certNumber || ""; break;
        case "shipperName": va = a.shipperName || ""; vb = b.shipperName || ""; break;
        case "productName": va = a.productName || ""; vb = b.productName || ""; break;
        case "periodTo": va = a.periodTo || ""; vb = b.periodTo || ""; break;
        case "available":
          va = (a.totalInvoices || 0) - usedOf(a);
          vb = (b.totalInvoices || 0) - usedOf(b);
          return sortDir === "asc" ? va - vb : vb - va;
        default: va = a[sortCol] || ""; vb = b[sortCol] || "";
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filtered, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const arrow = (col) => sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const allSelected = sorted.length > 0 && sorted.every(c => selected[c.id]);
  const toggleAll = () => { const ns = {}; if (!allSelected) sorted.forEach(c => { ns[c.id] = true; }); setSelected(ns); };
  const toggleOne = (id, e) => { if (e) e.stopPropagation(); setSelected(p => { const ns = { ...p }; if (ns[id]) delete ns[id]; else ns[id] = true; return ns; }); };
  const selectedCount = Object.keys(selected).length;
  const clearSelection = () => setSelected({});
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  /* Excel export. Summary = one row per certificate with usage totals.
     Detail = one row per usage entry (lot, shipper invoice, date) —
     matches the audit report grain. */
  const certsToExport = () => {
    const sel = sorted.filter(c => selected[c.id]);
    return sel.length > 0 ? sel : sorted;
  };
  const buildCertSummaryRows = () => {
    const freshShps = (function () { try { return JSON.parse(localStorage.getItem("gfi-shipments") || "[]"); } catch (e) { return []; } })();
    const freshBills = (function () { try { return JSON.parse(localStorage.getItem("gfi-bills") || "[]"); } catch (e) { return []; } })();
    return certsToExport().map(c => {
      const usage = computeCertUsage(c, freshShps, freshBills);
      const used = usage.length;
      const total = parseInt(c.totalInvoices, 10) || 0;
      return {
        "Certificate #": xs(c.certNumber),
        Supplier: xs(c.shipperName || c.supplierName),
        Product: xs(c.product),
        "Period From": xs(c.periodFrom),
        "Period To": xs(c.periodTo),
        Total: intOr(total),
        Used: intOr(used),
        Available: intOr(Math.max(0, total - used)),
        Status: xs(c.status),
      };
    });
  };
  const buildCertDetailRows = () => {
    const out = [];
    const freshShps = (function () { try { return JSON.parse(localStorage.getItem("gfi-shipments") || "[]"); } catch (e) { return []; } })();
    const freshBills = (function () { try { return JSON.parse(localStorage.getItem("gfi-bills") || "[]"); } catch (e) { return []; } })();
    certsToExport().forEach(c => {
      const meta = { "Certificate #": xs(c.certNumber), Supplier: xs(c.shipperName || c.supplierName), Product: xs(c.product) };
      const usage = computeCertUsage(c, freshShps, freshBills);
      if (usage.length === 0) {
        out.push({ ...meta, Type: "Cert", Lot: "", "Shipper Invoice": "", Date: "", Source: "" });
        return;
      }
      usage.forEach(entry => {
        out.push({
          ...meta,
          Type: "Usage",
          Lot: xs(entry.lot || (entry.lots ? entry.lots.join(", ") : "")),
          "Shipper Invoice": xs(entry.shipperInvoice),
          Date: xs(entry.date),
          Source: xs(entry.source || "shipment"),
        });
      });
    });
    return out;
  };
  const handleBulkDelete = async () => {
    if (saving) return;
    setSaving(true); setError(null);
    const ids = Object.keys(selected);
    try {
      await Promise.all(ids.map(id => deleteFromSupabase("export_certs", id)));
      const keep = certs.filter(c => !selected[c.id]);
      setCerts(keep); writeCertsCache(keep);
      setShowBulkDelete(false); setSelected({}); setToast(t.deleted);
    } catch (e) {
      setError("Could not bulk delete: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  // Alert counts
  const alertCounts = useMemo(() => {
    let low = 0, critical = 0, exhausted = 0;
    certs.forEach(c => {
      const level = getAlertLevel(c);
      if (level === "low") low++;
      if (level === "critical") critical++;
      if (level === "exhausted") exhausted++;
    });
    return { low, critical, exhausted, total: low + critical + exhausted };
  }, [certs]);

  if (loading) return null;

  const thStyle = { padding: "10px 10px", textAlign: "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", borderBottom: "2px solid var(--color-border)" };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--color-text)", margin: 0 }}>{t.title}</h2>
          <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t.showing} {sorted.length} {t.of} {certs.length} {t.total}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ExportButton lang={lang} filenameBase="ExportCertificates" selectedCount={selectedCount} totalCount={sorted.length} buildSummary={buildCertSummaryRows} buildDetail={buildCertDetailRows} />
          {canCreate && <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ {t.add}</button>}
        </div>
      </div>
      {error && <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", marginBottom: 12, fontSize: 12, color: "#B91C1C", fontWeight: 500 }}><span style={{ fontSize: 14 }}>⚠</span><span style={{ flex: 1, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word" }}>{error}</span><button onClick={reload} style={{ padding: "5px 12px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#B91C1C", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button></div>}

      {/* Alert summary */}
      {alertCounts.total > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          {alertCounts.exhausted > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
              <span style={{ fontSize: 12 }}>🚫</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#DC2626" }}>{alertCounts.exhausted} {lang === "es" ? "agotado(s)" : "exhausted"}</span>
            </div>
          )}
          {alertCounts.critical > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }}>
              <span style={{ fontSize: 12 }}>🔴</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#DC2626" }}>{alertCounts.critical} {lang === "es" ? "última factura" : "last invoice"}</span>
            </div>
          )}
          {alertCounts.low > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)" }}>
              <span style={{ fontSize: 12 }}>⚠️</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#D97706" }}>{alertCounts.low} {lang === "es" ? "por agotarse" : "running low"}</span>
            </div>
          )}
        </div>
      )}

      {/* Search & Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.search} style={{ ...inp, maxWidth: 280 }} />
        {!lockSupplierId && <SearchSelect value={filterShipper} onChange={setFilterShipper} placeholder={t.allShippers} style={{ minWidth: 160 }} options={uniqueShippers.map(s => ({ value: s, label: s }))} />}
        <SearchSelect value={filterProduct} onChange={setFilterProduct} placeholder={t.allProducts} style={{ minWidth: 140 }} options={uniqueProducts.map(p => ({ value: p, label: p }))} />
        <SearchSelect value={filterStatus} onChange={setFilterStatus} placeholder={t.all} style={{ minWidth: 140 }} options={[{value:"active",label:t.active},{value:"expiringSoon",label:t.expiringSoon},{value:"exhausted",label:t.exhausted},{value:"expired",label:t.expired}]} />
        <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} style={{ minWidth: 180 }} groups={buildSeasonFilterOptions(lang)} />
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--color-text-muted)", cursor: "pointer" }}>
          <input type="checkbox" checked={!showExpired} onChange={e => setShowExpired(!e.target.checked)} />
          {lang === "es" ? "Ocultar expirados" : "Hide expired"}
        </label>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title={lang === "es" ? "Desde" : "From"} style={{ ...inp, maxWidth: 140 }} />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} title={lang === "es" ? "Hasta" : "To"} style={{ ...inp, maxWidth: 140 }} />
        {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>}
      </div>

      {/* Selection summary bar — L: actions + selected counts of
          Total Invoices / Used / Available. R: module total over
          the filtered set. Standard pattern shared across modules. */}
      {selectedCount > 0 && (function () {
        var aggregate = function (list) {
          var out = { count: 0, totalInv: 0, used: 0, avail: 0 };
          (list || []).forEach(function (c) {
            var total = parseInt(c.totalInvoices, 10) || 0;
            var u = usedOf(c);
            out.count    += 1;
            out.totalInv += total;
            out.used     += u;
            out.avail    += Math.max(0, total - u);
          });
          return out;
        };
        var selectedRows = sorted.filter(function (c) { return selected[c.id]; });
        var sel = aggregate(selectedRows);
        var grand = aggregate(filtered);
        var num = function (n) { return <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{Number(n).toLocaleString()}</span>; };
        var numAvail = function (n) { return <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: n > 0 ? "#047857" : "var(--color-text-muted)" }}>{Number(n).toLocaleString()}</span>; };
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)", marginBottom: 10, fontSize: 12 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {canDelete && <button onClick={() => setShowBulkDelete(true)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"🗑 "}{lang === "es" ? "Eliminar" : "Delete Selected"}</button>}
              <button onClick={clearSelection} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>
            </div>
            <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 14, flexWrap: "wrap", fontSize: 11 }}>
              <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
              <span style={{ color: "var(--color-text-muted)" }}>|</span>
              <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Facturas Total" : "Total Invoices"}: </span>{num(sel.totalInv)}</span>
              <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Usadas" : "Used"}: </span>{num(sel.used)}</span>
              <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Disponibles" : "Available"}: </span>{numAvail(sel.avail)}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, minWidth: 220, paddingLeft: 14, borderLeft: "1px dashed var(--color-border)" }}>
              <div style={{ fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em" }}>{lang === "es" ? "Total módulo" : "Module total"}</div>
              <div style={{ display: "flex", gap: 10, fontSize: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <span title={lang === "es" ? "Certificados" : "Certs"}><strong style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text)" }}>{filtered.length}c</strong></span>
                <span title={lang === "es" ? "Facturas Total" : "Total Invoices"}>{num(grand.totalInv)}</span>
                <span title={lang === "es" ? "Usadas" : "Used"}>{num(grand.used)}</span>
                <span title={lang === "es" ? "Disponibles" : "Available"}>{numAvail(grand.avail)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Table */}
      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14 }}>{t.noResults}</div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 30, cursor: "default", textAlign: "center" }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                <th onClick={() => toggleSort("certNumber")} style={thStyle}>{t.certNumber}{arrow("certNumber")}</th>
                <th onClick={() => toggleSort("shipperName")} style={thStyle}>{t.shipper}{arrow("shipperName")}</th>
                <th onClick={() => toggleSort("productName")} style={thStyle}>{t.product}{arrow("productName")}</th>
                <th onClick={() => toggleSort("periodTo")} style={thStyle}>{t.period}{arrow("periodTo")}</th>
                <th onClick={() => toggleSort("available")} style={{ ...thStyle, textAlign: "center" }}>{t.available}{arrow("available")}</th>
                <th style={{ ...thStyle, textAlign: "center" }}>{t.status}</th>
                <th style={{ ...thStyle, textAlign: "center", width: 32 }}>📄</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => {
                const cUsed = usedOf(c);
                const avail = (c.totalInvoices || 0) - cUsed;
                const status = getStatus(c);
                const ss = STATUS_STYLES[status] || STATUS_STYLES.active;
                const statusLabel = t[status] || status;
                return (
                  <tr key={c.id} onClick={() => setViewing(c)} style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: "1px solid var(--color-border)", background: selected[c.id] ? "rgba(42,122,111,0.04)" : "transparent" }}
                    onMouseEnter={e => e.currentTarget.style.background = selected[c.id] ? "rgba(42,122,111,0.08)" : "var(--color-bg-hover)"}
                    onMouseLeave={e => e.currentTarget.style.background = selected[c.id] ? "rgba(42,122,111,0.04)" : "transparent"}>
                    <td style={{ padding: "10px 8px", textAlign: "center" }} onClick={e => e.stopPropagation()}><input type="checkbox" checked={!!selected[c.id]} onChange={e => toggleOne(c.id, e)} /></td>
                    <td style={{ padding: "10px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600, color: "var(--color-accent)" }}>{c.certNumber || "—"}</td>
                    <td style={{ padding: "10px 10px", fontSize: 12, fontWeight: 600 }}>{c.shipperName || "—"}</td>
                    <td style={{ padding: "10px 10px", fontSize: 12 }}>{c.productName || "—"}</td>
                    <td style={{ padding: "10px 10px", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(c.periodFrom)} → {fmtDate(c.periodTo)}</td>
                    <td style={{ padding: "10px 10px", textAlign: "center" }}>
                      <InvoiceBar total={c.totalInvoices || 0} used={cUsed} />
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "center" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: ss.bg, color: ss.text, border: `1px solid ${ss.border}`, boxShadow: STATUS_BEVEL, whiteSpace: "nowrap" }}>{statusLabel}</span>
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "center" }}>
                      {c.document ? <span style={{ fontSize: 14 }}>📄</span> : <span style={{ fontSize: 14, opacity: 0.2 }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewing && <DetailPanel cert={viewing} t={t} lang={lang} onEdit={handleEdit} onClose={() => setViewing(null)} canEdit={canEdit} />}
      {showForm && <CertForm cert={editing} certs={certs} usedCount={editing ? ((resolvedCerts.find(c => c.id === editing.id) || {}).usedCount || 0) : 0} t={t} lang={lang} lockSupplierId={lockSupplierId} canDelete={canDelete} onSave={handleSave} onDelete={handleDelete} onCancel={() => { setShowForm(false); setEditing(null); }} />}
      {showBulkDelete && <BulkDeleteDialog count={selectedCount} lang={lang} onCancel={() => setShowBulkDelete(false)} onConfirm={handleBulkDelete} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}